import {
  createStyles,
  FormControlLabel,
  makeStyles,
  Paper,
  Radio,
  RadioGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Theme,
  Typography,
} from '@material-ui/core';
import { IVerifyAddressResponse } from 'api/file/interfaces/verify-address';
import React, { useEffect, useState } from 'react';
import FormDrawerComponent from 'ui-kit/components/drawer/FormDrawerComponent';
import useSnackBars from 'ui-kit/components/notification/useSnackbars';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      padding: theme.spacing(3),
    },
    propertyCard: {
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: theme.shape.borderRadius,
      padding: theme.spacing(2),
      marginBottom: theme.spacing(2),
      '&:hover': {
        backgroundColor: theme.palette.action.hover,
      },
    },
    selectedProperty: {
      border: `2px solid ${theme.palette.primary.main}`,
      backgroundColor: theme.palette.action.selected,
    },
    propertyDetailLabel: {
      fontWeight: 'bold',
      marginRight: theme.spacing(1),
    },
    propertyDetailItem: {
      marginBottom: theme.spacing(1),
    },
    radioGroup: {
      width: '100%',
    },
    instructions: {
      marginBottom: theme.spacing(3),
    },
    addressBlock: {
      whiteSpace: 'pre-line',
      marginTop: theme.spacing(1),
    },
    tableContainer: {
      marginTop: theme.spacing(2),
    },
    tableHeader: {
      backgroundColor: theme.palette.background.paper,
    },
    tableHeaderCell: {
      fontWeight: 'bold',
      height: '40px !important',
    },
    verticalDivider: {
      position: 'relative',
      '&::after': {
        content: '""',
        position: 'absolute',
        top: theme.spacing(1),
        bottom: theme.spacing(1),
        right: 0,
        width: '1px',
        backgroundColor: theme.palette.grey[300],
      },
    },
    successAlert: {
      height: 20,
      padding: '0px 6px',
      alignItems: 'center',
      borderRadius: 100,
      background: '#E8F5E9',
      gap: 4,
      color: theme.palette.success.dark,
      width: 'fit-content',
      float: 'right',
      marginRight: theme.spacing(3),
      display: 'flex',
    },
    radioCell: {
      width: '48px',
      padding: theme.spacing(1),
    },
    addressCell: {
      padding: theme.spacing(1),
      width: '60%',
    },
    apnCell: {
      padding: theme.spacing(1),
    },
    tableRow: {
      '&:hover': {
        backgroundColor: theme.palette.action.selected,
      },
    },
    selectedRow: {
      backgroundColor: theme.palette.primary.light,
    },
    cardTitle: {
      fontWeight: 'bold',
      marginBottom: theme.spacing(1),
    },
  }),
);

export interface VerifiedPropertiesDrawerProps {
  properties: IVerifyAddressResponse[];
  propertyIndex: number;
  sourcePage: string;
  submitAction?: (selectedProperty: IVerifyAddressResponse, propertyIndex: number) => void;
}

const VerifiedPropertiesDrawer: React.FC<VerifiedPropertiesDrawerProps> = ({
  properties,
  propertyIndex,
  sourcePage,
  submitAction,
}) => {
  const classes = useStyles();
  const { addSnackbarMessage } = useSnackBars();

  const [selectedProperty, setSelectedProperty] = useState<IVerifyAddressResponse>(properties[0] || null);
  const [openVerifyDrawer, setOpenVerifyDrawer] = useState(true);
  const [isVerifySubmitted, setIsVerifySubmitted] = useState(false);

  const handlePropertySelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const propertyId = Number(event.target.value);

    const selected = properties.find((property) => property.propertyDetailId === propertyId);
    if (selected) {
      setSelectedProperty(selected);
    }
  };

  useEffect(() => {
    if (properties.length > 0) {
      setSelectedProperty(properties[0]);
      setOpenVerifyDrawer(true);
    }
  }, [properties]);

  // Helper function to format address for display in a single line
  const formatAddress = (property: IVerifyAddressResponse): string => {
    const parts = [property.address1, property.address2, property.city, `${property.stateCode} ${property.zip}`].filter(
      (part) => part && part.trim() !== '',
    );
    return parts.join(', ');
  };

  // Helper function to format address in multi-line format
  const formatAddressMultiline = (property: IVerifyAddressResponse): string => {
    const parts = [
      property.address1,
      property.address2,
      `${property.city}, ${property.stateCode} ${property.zip}`,
      `County: ${property.county}`,
      property.apn ? `APN: ${property.apn}` : '',
    ].filter((part) => part && part.trim() !== '');
    return parts.join('\n');
  };

  // Render property cards for exactly 2 addresses
  const renderTwoAddressesView = () => {
    return (
      <RadioGroup
        aria-label="verified properties"
        name="verified-properties"
        value={selectedProperty.propertyDetailId}
        onChange={handlePropertySelect}
        className={classes.radioGroup}
        data-testid="VerifiedPropertiesList"
      >
        {properties.map((property, index) => (
          <FormControlLabel
            key={property.propertyDetailId}
            value={property.propertyDetailId}
            control={<Radio color="primary" data-testid={`VerifyAddressOption${index + 1}`} />}
            label={
              <div
                className={`${classes.propertyCard} ${
                  selectedProperty?.propertyDetailId === property.propertyDetailId ? classes.selectedProperty : ''
                }`}
              >
                <Typography variant="subtitle1" className={classes.cardTitle}>
                  {index === 0 ? 'Address Entered' : 'Validated Address'}
                </Typography>
                <Typography variant="body1" className={classes.addressBlock}>
                  {formatAddressMultiline(property)}
                </Typography>
              </div>
            }
            className={classes.radioGroup}
          />
        ))}
      </RadioGroup>
    );
  };

  // Render table view for more than 2 addresses
  const renderTableView = () => {
    return (
      <TableContainer component={Paper} className={classes.tableContainer}>
        <Table aria-label="verified properties table">
          <TableHead className={classes.tableHeader}>
            <TableRow>
              <TableCell className={classes.tableHeaderCell}></TableCell>
              <TableCell className={`${classes.tableHeaderCell} ${classes.verticalDivider}`}>Address</TableCell>
              <TableCell className={classes.tableHeaderCell}>APN</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {properties.map((property, index) => (
              <TableRow
                key={property.propertyDetailId}
                className={`${classes.tableRow} ${
                  selectedProperty?.propertyDetailId === property.propertyDetailId ? classes.selectedRow : ''
                }`}
              >
                <TableCell className={classes.radioCell}>
                  <Radio
                    checked={selectedProperty?.propertyDetailId === property.propertyDetailId}
                    onChange={handlePropertySelect}
                    value={property.propertyDetailId}
                    name="property-radio-button"
                    color="primary"
                    data-testid={`VerifyAddressOption${index + 1}`}
                  />
                </TableCell>
                <TableCell className={classes.addressCell}>
                  {formatAddress(property)}
                  {index === 0 && (
                    <Typography variant="caption" className={classes.successAlert}>
                      Address Entered
                    </Typography>
                  )}
                </TableCell>
                <TableCell className={classes.apnCell}>
                  {property.apn}
                  {!property.apn && (
                    <Typography variant="caption" color="textSecondary">
                      Not Available
                    </Typography>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  return (
    <div className={classes.root}>
      <FormDrawerComponent
        title={'Confirm Address'}
        open={openVerifyDrawer}
        primaryActionProps={{
          loading: isVerifySubmitted,
          disabled: isVerifySubmitted,
          'data-testid': 'VerifyAddressSubmit',
          size: 'small',
        }}
        dismissActionProps={{
          disabled: isVerifySubmitted,
          'data-testid': 'VerifyAddressCancel',
          size: 'small',
        }}
        crossQAPrefix="VerifyAddress"
        primaryActionLabel={'Use Selected Address'}
        onPrimaryAction={() => {
          if (selectedProperty) {
            setIsVerifySubmitted(true);
            submitAction && submitAction(selectedProperty, propertyIndex);
          } else {
            addSnackbarMessage({
              html: true,
              message: `<b class="savingFailedErrorHeading">Select a validated address to use.</b>`,
              type: 'error',
            });
          }
        }}
        onDismissAction={() => {
          setOpenVerifyDrawer(false);
        }}
        testId="scrollable-form-drawer"
        width={960}
      >
        <Typography variant="body1" className={classes.instructions}>
          <>
            We found {properties.length == 2 ? 'a variation' : 'several variations'} of the property address you
            entered. Please review and select which address you&apos;d like to use for this transaction.
          </>
        </Typography>

        {properties.length === 2 ? renderTwoAddressesView() : renderTableView()}
      </FormDrawerComponent>
    </div>
  );
};

export default VerifiedPropertiesDrawer;
